import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import { PurchaseStatus } from "@/app/payments/types";
import { BannersPlacement } from "@/components/BannersPlacement/BannersPlacement";
import { Button } from "@/components/Button/Button";
import { PersistentDrawer } from "@/components/Drawer";
import { PromoCodeForm } from "@/components/PromoCodeForm/PromoCodeForm";
import { usePurchaseStateContext } from "@/contexts/PurchaseStateContext";
import { travellersQueryOptions } from "@/features/travellers/hooks/useTravellers";
import {
  allTravellerAgeGroups,
  type Traveller,
} from "@/features/travellers/types/types";
import { OverviewCard } from "@/features/trips/components/bookingFlow/summary/OverviewCard";
import { PriceDetailsCard } from "@/features/trips/components/bookingFlow/summary/PriceDetailsCard";
import { TermsAndConditionsCard } from "@/features/trips/components/bookingFlow/summary/TermsAndConditionsCard";
import { TripDetailsCard } from "@/features/trips/components/bookingFlow/summary/TripDetailsCard";
import { TripTourCard } from "@/features/trips/components/bookingFlow/tripDetails/TripTourCard";
import {
  TravellerListCard,
  TravellerListItem,
} from "@/features/trips/components/TravellerListCard";
import { useTripBooking } from "@/features/trips/hooks/useTripBooking";
import { useTripPrice } from "@/features/trips/hooks/useTripPrice";
import { tripQueryOptions } from "@/features/trips/hooks/useTrips";
import type { BookingData } from "@/features/trips/types/types";
import { usePromoCodeValidation } from "@/hooks/usePromoCodeValidation";
import {
  rudderAnalytics,
  TripTrackingEvents,
  PromoCodeTrackingEvents,
} from "@/lib/rudderstack";
import { BannersPlacementSlug } from "@/types/enums";
import { maskPassportNumber } from "@/utils/maskPassportNumber";

export const Route = createFileRoute("/_authenticated/trips/$tripId/summary")({
  component: RouteComponent,
});

function RouteComponent() {
  const intl = useIntl();
  const { tripId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { data: trip } = useSuspenseQuery(tripQueryOptions(tripId));
  const { updatePurchaseState } = usePurchaseStateContext();
  const { getFieldState, formState, handleSubmit, control } =
    useFormContext<BookingData>();
  const { invalid: termsAndConditionsInvalid } = getFieldState(
    "termsAndConditions",
    formState,
  );
  const { basePrice, discountAmount, tourPrice, totalPrice } =
    useTripPrice(trip);
  const { data: travellers } = useQuery(travellersQueryOptions());
  const travellerSlots = useWatch<BookingData, "selectedTravellers">({
    name: "selectedTravellers",
    control,
  });
  const promoCode = useWatch<BookingData, "promoCode">({
    name: "promoCode",
    control,
  });

  const travellersArray = useMemo(() => {
    const travellersById: Record<string, Traveller> = {};
    for (const traveller of travellers || []) {
      travellersById[traveller.id] = traveller;
    }

    return allTravellerAgeGroups
      .map((ageGroup) =>
        travellerSlots[ageGroup]
          .filter((x): x is NonNullable<typeof x> => Boolean(x))
          .map((travellerId, index) => {
            const traveller = travellersById[travellerId];

            return {
              id: traveller.id,
              name: `${traveller.firstName} ${traveller.lastName}`,
              additionalData: [
                intl.formatMessage(
                  {
                    defaultMessage:
                      "{type, select, ADT {Adult} CHD {Child} INF {Infant} other {Traveller}} {number}",
                    description:
                      "Numbered traveller slot label (e.g. 'Adult 1', 'Child 2')",
                  },
                  {
                    type: ageGroup,
                    number: index + 1,
                  },
                ),
                traveller.passportNumber
                  ? maskPassportNumber(traveller.passportNumber)
                  : null,
              ].filter((item) => item !== null),
            };
          }),
      )
      .flat();
  }, [travellers, travellerSlots, intl]);

  const {
    qiPayment: { isPending: isQiPaymentPending, mutateAsync },
  } = useTripBooking({
    onPaymentSuccess: ({ orderId }) => {
      rudderAnalytics.track(TripTrackingEvents.USER_SUCCESSFULY_PAID, {
        trip_id: tripId,
        trip_name: trip.name,
        order_id: orderId,
      });
    },
    onPaymentFailure: ({ errorType, errorMessage }) => {
      rudderAnalytics.track(TripTrackingEvents.USER_UNSUCCESSFULY_PAID, {
        trip_id: tripId,
        trip_name: trip.name,
        error_type: errorType,
        error_message: errorMessage,
      });
    },
    onSuccess: ({ orderId, status }) => {
      if (status === PurchaseStatus.SUCCESSFUL) {
        navigate({ to: "../success", search: { orderId } }).then(() => {
          updatePurchaseState("completed");
        });
      }
    },
  });

  const { data: promoCodeData, validationStatus: promoCodeValidationStatus } =
    usePromoCodeValidation({
      service: "trips",
      validationRequestData: {
        promoCode: promoCode || "",
        packageId: tripId,
        packageAmount: totalPrice,
      },
      onCodeApplied: (promoCode, data) => {
        rudderAnalytics.track(
          PromoCodeTrackingEvents.USER_PROMO_CODE_APPLIED_SUCCESSFULLY,
          {
            flow_type: "package",
            promo_code: promoCode,
            discount_amount: data.discountAmount,
          },
        );
      },
      onCodeFailure: (promoCode, errorMessage) => {
        rudderAnalytics.track(
          PromoCodeTrackingEvents.USER_PROMO_CODE_APPLICATION_FAILED,
          {
            flow_type: "package",
            promo_code: promoCode,
            error_message: errorMessage,
          },
        );
      },
    });

  useEffect(() => {
    rudderAnalytics.track(TripTrackingEvents.USER_REVIEWED_TRIP_SUMMARY, {
      trip_id: tripId,
      trip_name: trip.name,
      total_price: basePrice,
    });
  }, [tripId, trip.name, basePrice]);

  return (
    <>
      <h2 className="mb-4 text-xl font-medium text-text-title">
        {intl.formatMessage({
          defaultMessage: "Booking summary",
          description: "Page heading for the trip booking summary",
        })}
      </h2>

      <div className="space-y-3">
        <OverviewCard trip={trip} />

        <TripDetailsCard trip={trip} />

        {useFeatureIsOn("optional-tours") && trip.hasOptionalTours && (
          <TripTourCard trip={trip} source="summary" />
        )}

        <TravellerListCard>
          {travellersArray.map(({ id, name, additionalData }) => (
            <TravellerListItem
              key={id}
              name={name}
              additionalData={additionalData}
            />
          ))}
        </TravellerListCard>

        <BannersPlacement
          slug={BannersPlacementSlug.CHECKOUT}
          analyticsContext={{
            context: "tour",
            trip_id: trip.id,
          }}
        />

        {useFeatureIsOn("promo-codes") ? (
          <Controller
            name="promoCode"
            control={control}
            render={({ field: { value, onChange } }) => (
              <PromoCodeForm
                appliedCode={value}
                validationStatus={promoCodeValidationStatus}
                onCodeApply={({ code }) => {
                  rudderAnalytics.track(
                    PromoCodeTrackingEvents.USER_SUBMIT_PROMO_CODE,
                    { promo_code: code, flow_type: "package" },
                  );

                  onChange(code);
                }}
                onCodeRemove={({ code, isApplied }) => {
                  if (isApplied) {
                    rudderAnalytics.track(
                      PromoCodeTrackingEvents.USER_REMOVE_PROMO_CODE,
                      {
                        promo_code: code,
                        discount_amount_removed: promoCodeData?.result
                          ? (promoCodeData.discountAmount ?? null)
                          : null,
                        flow_type: "package",
                      },
                    );
                  }

                  onChange("");
                }}
                onFocus={(hasAppliedPromoCode) => {
                  if (!hasAppliedPromoCode) {
                    rudderAnalytics.track(
                      PromoCodeTrackingEvents.USER_SELECTED_PROMO_CODE,
                      { flow_type: "package" },
                    );
                  }
                }}
              />
            )}
          />
        ) : null}

        <PriceDetailsCard
          basePrice={basePrice}
          baseDiscount={discountAmount}
          tourPrice={tourPrice}
          promoCodeDiscount={promoCodeData?.discountAmount}
          totalPrice={promoCodeData?.paymentAmount || totalPrice}
        />

        <Controller<BookingData, "termsAndConditions">
          name="termsAndConditions"
          render={({ field: { onChange, onBlur, value } }) => (
            <TermsAndConditionsCard
              checked={value}
              onBlur={onBlur}
              onChange={onChange}
            />
          )}
        />
      </div>

      <PersistentDrawer>
        <Button
          fullWidth
          variant="contained"
          disabled={
            termsAndConditionsInvalid ||
            promoCodeValidationStatus?.type === "loading"
          }
          loading={isQiPaymentPending}
          onClick={() => {
            handleSubmit((data) =>
              mutateAsync({
                packageId: tripId,
                startDate: data.tripDate,
                travelersIds: Object.values(data.selectedTravellers)
                  .flat()
                  .filter((x): x is NonNullable<typeof x> => Boolean(x)),
                numberOfAdultsSingleRoom: data.travellers.adultInSingleRoom,
                numberOfAdultsDoubleRoom: data.travellers.adultInDoubleRoom,
                numberOfChildrenWithBed: data.travellers.childWithBed,
                numberOfChildrenWithoutBed: data.travellers.childWithoutBed,
                numberOfInfants: data.travellers.infant,
                promoCode: promoCodeData?.result
                  ? promoCode || undefined
                  : undefined,
                includesCityTour: data.isTripTourAdded,
              }),
            )();
          }}
        >
          {intl.formatMessage({
            defaultMessage: "Book the trip",
            description:
              "Button to confirm and book the trip on the booking page",
          })}
        </Button>
      </PersistentDrawer>
    </>
  );
}
